import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/lib/brand";

const LINKS = [
  ["story", "Story"],
  ["shop", "Shop"],
  ["craft", "Craft"],
  ["lookbook", "Lookbook"],
  ["support", "Support"],
  ["speak-up", "Speak Up"],
];

export const Nav = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const go = (id) => {
    setMenu(false);
    scrollToId(id);
  };

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled ? "bg-[#07090D]/75 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"}`}>
      <a href="tel:116123" data-testid="crisis-bar-link" className="block bg-[var(--mth-blue)] text-[#04101f] text-center font-mono text-[11px] sm:text-xs tracking-[0.18em] uppercase py-1.5 hover:bg-white transition-colors">
        <Phone className="inline h-3 w-3 mr-2 -mt-0.5" /> Struggling right now? Samaritans — call 116 123, free, 24/7
      </a>
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo onClick={() => (window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo(0, 0))} />
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map(([id, label]) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)} className="link-underline font-mono text-xs uppercase tracking-[0.22em] text-slate-300 hover:text-white transition-colors">
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button data-testid="cart-open-button" onClick={() => setOpen(true)} className="relative h-10 px-4 rounded-full border border-white/15 hover:border-[var(--mth-blue)] flex items-center gap-2 transition-colors">
            <ShoppingBag className="h-4 w-4" />
            <span className="font-mono text-xs uppercase tracking-widest hidden sm:inline">Bag</span>
            <motion.span key={count} initial={{ scale: 0.4 }} animate={{ scale: 1 }} data-testid="cart-count" className="min-w-5 h-5 px-1 rounded-full bg-[var(--mth-blue)] text-[#04101f] text-[11px] font-bold grid place-items-center">
              {count}
            </motion.span>
          </button>
          <button data-testid="mobile-menu-toggle" onClick={() => setMenu((m) => !m)} className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-white/15">
            {menu ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden bg-[#07090D]/95 backdrop-blur-xl border-t border-white/10">
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map(([id, label]) => (
                <button key={id} data-testid={`mobile-nav-link-${id}`} onClick={() => go(id)} className="text-left font-display text-2xl font-extrabold uppercase">
                  {label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
